import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../components/Layout";
import { api, formatINR } from "../lib/api";
import { useHasPermission } from "../context/AuthContext";
import { Lock, ChevronRight, ShieldCheck, Search } from "lucide-react";

/**
 * Phase F3 — Confidential Vault.
 * Renders ONLY for holders of `view_confidential`. Non-permitted users landing
 * here via a stale link get a soft 404 message.
 */
export default function ConfidentialVault() {
  const navigate = useNavigate();
  const canView = useHasPermission("view_confidential");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    (async () => {
      try {
        const r = await api.get("/confidential/requests");
        setData(r.data);
      } catch {
        setData(null);
      } finally { setLoading(false); }
    })();
  }, [canView]);

  if (!canView) {
    return (
      <AppShell>
        <div className="max-w-md mx-auto text-center py-24" data-testid="confidential-denied">
          <div className="text-sm text-slate-500">Not found</div>
        </div>
      </AppShell>
    );
  }

  const docs = (data?.docs || []).filter((d) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      (d.vendor_name || "").toLowerCase().includes(needle) ||
      (d.request_id || "").toLowerCase().includes(needle) ||
      (d.notes || "").toLowerCase().includes(needle) ||
      (d.site || "").toLowerCase().includes(needle) ||
      (d.category || "").toLowerCase().includes(needle)
    );
  });

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="confidential-vault">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
              <Lock size={20} />
            </div>
            <div>
              <h1 className="font-display font-semibold text-2xl tracking-tight text-slate-900">
                Confidential
              </h1>
              <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                <ShieldCheck size={12} className="text-olive" />
                Private view · sealed from all normal reports
              </div>
            </div>
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total records" value={data?.totals?.count ?? 0} suffix="" />
          <StatCard label="Approved · all time" value={formatINR(data?.totals?.approved ?? 0)} />
          <StatCard label="Paid · YTD" value={formatINR(data?.totals?.ytd_paid ?? 0)} highlight />
          <StatCard label="Paid · MTD" value={formatINR(data?.totals?.mtd_paid ?? 0)} />
        </div>

        {/* Site breakdown */}
        {(data?.sites || []).length > 0 && (
          <div
            data-testid="confidential-sites"
            className="rounded-2xl border border-slate-200 bg-white p-5 lg:p-6"
          >
            <div className="text-xs font-medium tracking-wider uppercase text-slate-500 mb-3">
              By site
            </div>
            <div className="flex flex-wrap gap-2">
              {data.sites.map((s) => (
                <div
                  key={s.site}
                  data-testid={`confidential-site-${s.site}`}
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700"
                >
                  {s.site} · <span className="text-olive font-semibold">{formatINR(s.amount)}</span> · <span className="text-slate-400">{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="confidential-search"
            placeholder="Search by REQ ID, vendor, note, site, category…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-olive/30"
          />
        </div>

        {/* List */}
        <div className="space-y-2" data-testid="confidential-list">
          {loading ? (
            <div className="py-12 text-center text-sm text-slate-400">Loading…</div>
          ) : docs.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-400">
              No confidential expenses{q ? " match your search" : " yet"}.
            </div>
          ) : (
            docs.map((r) => <VaultRow key={r.request_id} r={r} onClick={() => navigate(`/requests/${r.request_id}`)} />)
          )}
        </div>
      </div>
    </AppShell>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div
      data-testid={`stat-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
      className={`rounded-2xl border p-4 ${highlight ? "border-olive/40 bg-olive/5" : "border-slate-200 bg-white"}`}
    >
      <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 font-display font-semibold text-xl text-slate-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function VaultRow({ r, onClick }) {
  const amt = Math.round(r.approved_amount ?? r.amount ?? 0);
  const paid = Math.round(r.total_paid ?? r.paid_amount ?? 0);
  return (
    <button
      onClick={onClick}
      data-testid={`confidential-row-${r.request_id}`}
      className="w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3 hover:border-slate-300 hover:bg-slate-50 transition"
    >
      <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0">
        <Lock size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-sm text-slate-900 truncate">{r.vendor_name || "—"}</div>
          <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
            {r.site} · {r.category}
          </div>
        </div>
        <div className="text-xs text-slate-500 mt-0.5 truncate">
          {r.request_id} · {r.status}{r.notes ? ` · ${r.notes}` : ""}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-semibold text-sm text-slate-900 tabular-nums">
          {formatINR(amt)}
        </div>
        {paid > 0 && (
          <div className="text-[11px] text-olive tabular-nums">
            paid {formatINR(paid)}
          </div>
        )}
      </div>
      <ChevronRight size={16} className="text-slate-300" />
    </button>
  );
}
